// Implements: F-CNV-003, F-UIX-009, F-UIX-010
/**
 * Who owns the mockup right now: `GET .../conversation` under its own key, so a turn
 * that ends (or starts) refreshes it by invalidating that key and nothing else has
 * to remember.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { abortableDelay as wait } from "../../../shared/api/abortableDelay";
import type { ApiError } from "../../../shared/api/ApiError";
import { backoffDelay } from "../../../shared/api/followRun";
import type { StreamRetry } from "../../../shared/api/followRun";
import { conversationStateKey } from "../../../shared/api/queryKeys";
import type { ConversationStateView } from "../../../types/api";
import { fetchConversationState } from "../api/conversationApi";

/** The refinement status the server reports while a turn owns the mockup. */
export const GENERATING = "generating";

export function useConversationState(mockupId: string): ConversationStateView | undefined {
  const query = useQuery<ConversationStateView, ApiError>({
    queryKey: conversationStateKey(mockupId),
    queryFn: () => fetchConversationState(mockupId),
  });
  return query.data;
}

/**
 * Read the state again, with a growing pause and a bounded number of times, while
 * `waiting` holds: a screen that follows no run (the turn is another tab's, or has no
 * run yet) would otherwise stay locked until somebody reloads. It stops the moment
 * `waiting` turns false and when the screen goes.
 */
export function usePollWhile(mockupId: string, waiting: boolean, retry: StreamRetry): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!waiting) return undefined;
    const controller = new AbortController();
    const stopped = (): boolean => controller.signal.aborted;
    void (async () => {
      for (let attempt = 0; attempt < retry.attempts && !stopped(); attempt += 1) {
        await wait(backoffDelay(retry, attempt), controller.signal);
        if (stopped()) return;
        await queryClient.invalidateQueries({ queryKey: conversationStateKey(mockupId) });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [mockupId, waiting, retry, queryClient]);
}
